import { Col, Row, Stack } from "react-bootstrap";
import BackgroundImage from "../atoms/BackgroundImage";

function LayoutBase({
  children,
  className,
  padding = true,
  footnote,
}: {
  children: JSX.Element | JSX.Element[] | undefined;
  className?: string;
  padding?: boolean;
  footnote?: JSX.Element | JSX.Element[] | string;
}): JSX.Element {
  return (
    <>
      <Stack
        className={`position-relative w-100 min-h-100 ${
          padding ? "py-5" : "py-0"
        } justify-content-center align-items-center mb-3 shadow ${className}`}
      >
        {children}
        {footnote ? (
          <div className="w-100 px-3 px-md-5">{footnote}</div>
        ) : (
          <></>
        )}
      </Stack>
    </>
  );
}

interface layoutProps {
  heading?: JSX.Element | JSX.Element[];
  body?: JSX.Element | JSX.Element[];
  bgSrc?: string;
  bgColor?: string;
  footnote?: JSX.Element | JSX.Element[] | string;
}

function Vertical({
  heading,
  body,
  bgSrc,
  bgColor,
  footnote,
}: layoutProps): JSX.Element {
  return (
    <>
      <LayoutBase footnote={footnote}>
        <Row className="px-md-5 px-3">
          <Col xs={12}>
            <>{heading}</>
            <>{body}</>
          </Col>
        </Row>
        <BackgroundImage src={bgSrc} color={bgColor} />
      </LayoutBase>
    </>
  );
}

function Horizontal({
  heading,
  body,
  bgSrc,
  bgColor,
  footnote,
}: layoutProps): JSX.Element {
  return (
    <>
      <LayoutBase footnote={footnote} className="px-md-5 px-3">
        <Row className="gx-4 w-100">
          <Col xs={0} md={1}></Col>
          <Col
            xs={12}
            md={5}
            className="d-flex align-items-center ps-md-5 ps-0"
          >
            {heading}
          </Col>
          <Col xs={12} md={5} className="d-flex flex-column">
            {body}
          </Col>
          <Col xs={0} md={1}></Col>
        </Row>
        <BackgroundImage src={bgSrc} color={bgColor} />
      </LayoutBase>
    </>
  );
}

function HorizontalBgImage({
  heading,
  bgSrc,
  bgColor,
  bgSize = "cover",
  footnote,
  className = "",
}: layoutProps & { bgSize?: string; className?: string }): JSX.Element {
  return (
    <>
      <LayoutBase
        className={`d-block ${className}`}
        padding={false}
        footnote={footnote}
      >
        <Row className="w-100 vh-100 m-0">
          <Col xs={0} md={1}></Col>
          <Col xs={12} md={5} className="d-flex align-items-center">
            {heading}
          </Col>
          <Col
            md={6}
            className="d-md-flex d-none min-h-100 flex-column p-0"
            style={{
              backgroundImage: `url(${bgSrc})`,
              backgroundSize: bgSize,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundColor: bgColor,
            }}
          ></Col>
        </Row>
        <BackgroundImage
          className="d-block d-md-none opacity-25"
          src={bgSrc}
          zIndex={"auto"}
        />
        <BackgroundImage className="d-none d-md-block" color={bgColor} />
      </LayoutBase>
    </>
  );
}

function Empty({
  children,
  bgSrc,
  footnote,
}: {
  children: JSX.Element | JSX.Element[] | undefined;
  bgSrc?: string;
  footnote?: JSX.Element | JSX.Element[] | string;
}) {
  return (
    <LayoutBase footnote={footnote}>
      <>
        {children}
        {bgSrc ? <BackgroundImage src={bgSrc} /> : <></>}
      </>
    </LayoutBase>
  );
}

const Layout: {
  Vertical: ({ heading, body, bgSrc }: layoutProps) => JSX.Element;
  Horizontal: ({ heading, body }: layoutProps) => JSX.Element;
  HorizontalBgImage: ({
    heading,
    bgSrc,
    bgColor,
    bgSize,
    className,
  }: layoutProps & { bgSize?: string; className?: string }) => JSX.Element;
  Empty: ({
    children,
    bgSrc,
    footnote,
  }: {
    children: JSX.Element | JSX.Element[] | undefined;
    bgSrc?: string;
    footnote?: JSX.Element | JSX.Element[] | string;
  }) => JSX.Element;
} = {
  Vertical,
  Horizontal,
  HorizontalBgImage,
  Empty,
};

export default Layout;
